import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, Observable, Subject, take, takeUntil, combineLatest, catchError, of } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { ICurrencyPrice, IInventoryFilter, IProduct, IProductCurrency } from 'src/app/features/produits/models/products.models';
import { ClearProducts, FetchProductsBySearch, SetInventoryFilter } from 'src/app/features/produits/store/produits.actions';
import { CatalogsService } from '../../../services/catalogs.services';
import { ProductsService } from 'src/app/features/produits/services/produits.services';
import { parseInventoryFilterRequest, refillInventoryFilter } from 'src/app/shared/utils/helpers';
import { FilterDialogComponent } from 'src/app/features/produits/components/filter-dialog/filter-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { INewCatalog } from '../new-catalog.component';
import { IPagination } from 'src/app/shared/models/page.model';
import { SetCurrentElementId } from 'src/app/shared/store/shared.actions';
import { ICatalog } from '../../../models/catalogs.models';
import { UpdateCatalog, UpdateCatalogSuccess } from '../../../store/catalogs.actions';
import { Actions, ofType } from '@ngrx/effects';
import { FetchAllCurrencies, FetchAllCurrenciesSuccess, FetchAllCurrenciesError } from 'src/app/features/customers/store/customers.actions';
import { areSelectedProductsValid, getValidationTooltipMessage } from 'src/app/shared/validators/products-prices-validators';
import { ICurrency } from 'src/app/features/customers/models/customers.models';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { SelecteableProductsListComponent } from './selecteable-products-list/selecteable-products-list.component';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';
import { HasPermissionContext } from 'src/app/shared/has-permission.directive';

@Component({
  selector: 'app-new-catalog-add-products',
  templateUrl: './new-catalog-add-products.component.html',
  styleUrls: ['./new-catalog-add-products.component.scss']
})
export class NewCatalogAddProductsComponent implements OnInit {
  products$: Observable<IProduct[]> = this.store.select((state)=> state.products.productsBySearch);
  currencies$: Observable<ICurrency[]> = this.store.select((state)=> state.customers.allCurrencies);
  availableCurrencies: ICurrency[] = [];
  productsMetadata$: Observable<IPagination | null> = this.store.select((state)=> state.products.metadata);
  products: IProduct[] = [];
  productsMetadata: IPagination | null = null;
  areProductsValid = true;
  source: 'agents' | 'catalogs' = 'catalogs';
  agentId!: number;
  pricingRules: any = null;
  selectedProducts: IProduct[] = [];
  selectedProductsFromCatalog: IProduct[] = [];
  selectedProductIds: Set<number> = new Set();
  productsFromSearch: IProduct[] = [];
  catalogId!: number;
  filter: IInventoryFilter = refillInventoryFilter();
  destroy$ = new Subject<void>();
  data: INewCatalog | null = null;
  currentPage = 1;
  pageSize = 50;
  isLoadingMore = false;
  searchTerm = '';
  isSelectingAll = false;
  selectingAllMessage = '';
  hasSelectedAllProducts = false;
  
  @ViewChild(SelecteableProductsListComponent) selectableProductsListComponent!: SelecteableProductsListComponent;
  
  isCurrenciesLoaded = false;
  isCatalogDataLoaded = false;
  canCreate = false;
  canEdit = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private catalogsService: CatalogsService,
    private productsService: ProductsService,
    private dialog: MatDialog,
    private actions$: Actions,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.store.select(selectCanDo('catalog', 'create')).pipe(takeUntil(this.destroy$)).subscribe((canCreate) => {
      this.canCreate = canCreate;
    });
    this.store.select(selectCanDo('catalog', 'edit')).pipe(takeUntil(this.destroy$)).subscribe((canEdit) => {
      this.canEdit = canEdit;
    });
    // Load currencies first if needed
    this.loadCurrenciesIfNeeded(() => {
      this.initializeComponent();
    });
  }

  private loadCurrenciesIfNeeded(callback?: () => void): void {
    this.currencies$.pipe(take(1)).subscribe((currencies) => {
      if (!currencies || currencies.length === 0) {
        this.isCurrenciesLoaded = false;
        this.store.dispatch(FetchAllCurrencies());
        this.actions$.pipe(
          ofType(FetchAllCurrenciesSuccess, FetchAllCurrenciesError),
          take(1)
        ).subscribe((action) => {
          if (action.type === FetchAllCurrenciesSuccess.type) {
            this.onCurrenciesLoadedSuccess(callback);
          } else {
            this.onCurrenciesLoadedError(callback);
          }
        });
      } else {
        this.isCurrenciesLoaded = true;
        if (callback) callback();
      }
    });
  }

  private onCurrenciesLoadedSuccess(callback?: () => void): void {
    this.isCurrenciesLoaded = true;
    if (callback) callback();
  }

  private onCurrenciesLoadedError(callback?: () => void): void {
    this.isCurrenciesLoaded = false;
    if (callback) callback();
  }

  private initializeComponent(): void {
    this.source = this.router.url.includes('agents') ? 'agents' : 'catalogs';
    if (this.source === 'agents') {
      this.route.queryParams
        .pipe(takeUntil(this.destroy$))
        .subscribe(params => {
          const { agentId, agent_id } = params;
          this.agentId = +(agentId ?? agent_id);
        });
    }
    const parentRoute = this.route.parent;
    const routeWithId = parentRoute ?? this.route;
    routeWithId.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.catalogId = +params['id'];
        this.store.dispatch(SetCurrentElementId({ currentElementId: this.catalogId }));
        
        this.loadCatalogDataIfNeeded();
      });

    this.setupProductSubscriptions();
    this.setupCatalogData();
  }

  private loadCatalogDataIfNeeded(): void {
    this.data = this.catalogsService.getNewCatalogData();
    
    if (!this.data && this.catalogId) {
      this.isCatalogDataLoaded = false;
      this.loadCatalogDataFromServer();
    } else if (this.data && this.catalogId) {
      // Check if cached data has all products loaded
      const totalProductIds = this.data.product_ids?.length || 0;
      const loadedProductsCount = this.data.products?.length || 0;
      
      // If we have product_ids but not all products loaded, reload from server
      if (totalProductIds > 0 && loadedProductsCount < totalProductIds) {
        // Reload all products from server to ensure we have complete data
        this.isCatalogDataLoaded = false;
        this.loadCatalogDataFromServer();
        return;
      }
      
      // Cached data is complete, use it
      this.isCatalogDataLoaded = true;
      // Store selected products from cached data
      this.selectedProductsFromCatalog = this.data.products || [];
      this.finalizeCatalogDataLoading();
    } else {
      this.isCatalogDataLoaded = true;
      this.data = this.catalogsService.getNewCatalogData();
      if(!this.data) {
        const catalogData: INewCatalog = {
          name: '',
          catalogtId: Number(this.catalogId),
          description: '',
          status: 'Draft',
          cover: new File([], ''),
          coverUrl: null,
          expiration_date: '',
          pricing_rule: 'discount',
          overall_pricing_value: 0,
          extra_discount: null,
          has_extra_discount: false,
          extra_discount_blocking: false,
          min_order_amount: null,
          products: [],
          agents: [],
          customers: [],
          locations: [],
          companies: [],
          quantity_rules: {
            increment: 1,
            max_qte: null,
            min_qte: 1,
            general_discount: null,
            can_be_sold_out_of_stock: false,
            show_stock: false,
          },
          locations_ids: [],
          is_multi_currency: false,
          default_currency: LocalStorageService.get('currentUser')?.user?.currency?.id,
          enabled_currencies: [],
        };

        this.catalogsService.setNewCatalogData(catalogData);
      }
      this.finalizeCatalogDataLoading();
    }
  }

  private loadCatalogDataFromServer(): void {
    // First, get catalog info
    this.catalogsService.getCatalogById(this.catalogId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((catalogResponse) => {
        if (!catalogResponse.object) {
          this.isCatalogDataLoaded = false;
          return;
        }
        
        const catalog = catalogResponse.object;
        const totalProducts = catalog.product_ids?.length || 0;
        
        // Fetch ALL selected products from catalog (handle pagination)
        if (totalProducts === 0) {
          // No products, just set catalog data
          const newCatalogData: INewCatalog = {
            subsidiary_id: catalog.subsidiary_id,
            name: catalog.name,
            catalogtId: catalog.id,
            description: catalog.description || '',
            status: catalog.status,
            cover: null as any,
            coverUrl: catalog.image || null,
            expiration_date: catalog.end_date || '',
            pricing_rule: catalog.overall_pricing_rule === 'margin_on_cost' ? 'margin' : 'discount',
            overall_pricing_value: catalog.overall_pricing_value || 0,
            extra_discount: catalog.max_extra_discount || null,
            has_extra_discount: !!catalog.max_extra_discount,
            extra_discount_blocking: catalog.block_order_on_max_extra_discount || false,
            min_order_amount: catalog.min_order_amount || null,
            products: [],
            agents: catalog.agents || [],
            customers: [],
            locations: catalog.locations || [],
            companies: catalog.companies || [],
            quantity_rules: {
              increment: catalog.increment,
              max_qte: catalog.max_qte,
              min_qte: catalog.min_qte,
              general_discount: catalog.general_discount,
              can_be_sold_out_of_stock: catalog.can_be_sold_out_of_stock,
              show_stock: catalog.show_stock,
            },
            locations_ids: catalog.locations_ids || [],
            product_ids: catalog.product_ids || [],
            loaded_product_ids: [],
            is_multi_currency: catalog.is_single_currency === false,
            default_currency: catalog.default_currency?.id,
            enabled_currencies: catalog.currencies?.map((currency: any) => currency.id),
            show_full_pricing_data: (catalog as any).show_full_pricing_data ?? true,
          };
          
          this.catalogsService.setNewCatalogData(newCatalogData);
          this.data = newCatalogData;
          this.isCatalogDataLoaded = true;
          this.finalizeCatalogDataLoading();
          return;
        }
        
        // Fetch all products by pagination
        const pageSize = 100;
        const totalPages = Math.ceil(totalProducts / pageSize);
        const productRequests = [];
        
        for (let page = 1; page <= totalPages; page++) {
          productRequests.push(
            this.catalogsService.GetCatalogProducts(this.catalogId, page, pageSize, false) // includeDeals=false for faster loading
          );
        }
        
        // Combine all product requests
        combineLatest(productRequests)
          .pipe(takeUntil(this.destroy$))
          .subscribe((responses) => {
            // Flatten all products from all pages
            const allProducts: IProduct[] = [];
            responses.forEach((response) => {
              if (response?.data) {
                allProducts.push(...response.data);
              }
            });
            
            // Store selected products separately
            this.selectedProductsFromCatalog = allProducts;
            
            const newCatalogData: INewCatalog = {
              subsidiary_id: catalog.subsidiary_id,
              name: catalog.name,
              catalogtId: catalog.id,
              description: catalog.description || '',
              status: catalog.status,
              cover: null as any,
              coverUrl: catalog.image || null,
              expiration_date: catalog.end_date || '',
              pricing_rule: catalog.overall_pricing_rule === 'margin_on_cost' ? 'margin' : 'discount',
              overall_pricing_value: catalog.overall_pricing_value || 0,
              extra_discount: catalog.max_extra_discount || null,
              has_extra_discount: !!catalog.max_extra_discount,
              extra_discount_blocking: catalog.block_order_on_max_extra_discount || false,
              min_order_amount: catalog.min_order_amount || null,
              products: allProducts,
              agents: catalog.agents || [],
              customers: [],
              locations: catalog.locations || [],
              companies: catalog.companies || [],
              quantity_rules: {
                increment: catalog.increment,
                max_qte: catalog.max_qte,
                min_qte: catalog.min_qte,
                general_discount: catalog.general_discount,
                can_be_sold_out_of_stock: catalog.can_be_sold_out_of_stock,
                show_stock: catalog.show_stock,
              },
              locations_ids: catalog.locations_ids || [],
              product_ids: catalog.product_ids || [],
              // Don't store loaded_product_ids when loading all products - it causes performance issues with large catalogs
              // Storing millions of product IDs in memory will tank browser performance
              // loaded_product_ids is only needed for tracking paginated loads, not full catalog loads
              loaded_product_ids: [],
              is_multi_currency: catalog.is_single_currency === false,
              default_currency: catalog.default_currency?.id,
              enabled_currencies: catalog.currencies?.map((currency: any) => currency.id),
            };
            
            this.catalogsService.setNewCatalogData(newCatalogData);
            this.data = newCatalogData;
            this.isCatalogDataLoaded = true;
            this.finalizeCatalogDataLoading();
          });
      });
  }

  private finalizeCatalogDataLoading(): void {
    this.data = this.catalogsService.getNewCatalogData();
    // Use selectedProductsFromCatalog if available, otherwise fall back to data.products
    this.selectedProducts = this.selectedProductsFromCatalog.length > 0 
      ? this.selectedProductsFromCatalog 
      : (this.data?.products || []);
    
    // Initialize selected product IDs from catalog data
    this.selectedProductIds = new Set(
      (this.selectedProducts || [])
        .map(p => p.id ?? p.product_id ?? 0)
        .filter(id => id > 0)
    );
    
    this.currencies$.pipe(take(1)).subscribe((list) => {
      this.availableCurrencies = list.filter((currency) => this.data?.enabled_currencies?.includes(currency.id)) || [];
      if(!this.availableCurrencies.some((currency) => currency.id === this.data?.default_currency)) {
        if(this.data?.default_currency) {
          this.availableCurrencies.push({
            id: this.data.default_currency,
            name: list.find((currency) => currency.id === this.data?.default_currency)?.name || '',
            full_name: list.find((currency) => currency.id === this.data?.default_currency)?.full_name || '',
            symbol: list.find((currency) => currency.id === this.data?.default_currency)?.symbol || '',
            is_default: list.find((currency) => currency.id === this.data?.default_currency)?.is_default || false,
            is_active: list.find((currency) => currency.id === this.data?.default_currency)?.is_active || false,
          });
        }
      }
    });

    if (this.catalogId) {
      setTimeout(() => {
        const filterRequest = parseInventoryFilterRequest(this.filter);
        this.store.dispatch(
          FetchProductsBySearch({
            keyword: this.searchTerm || '',
            page: { number: this.currentPage, size: this.pageSize },
            filter: filterRequest,
            sort_by: 'name',
            sort_mode: 'asc',
            priority: this.getPriorityProductIds(),
            includeDeals: false,
          })
        );
      }, 100);
    }
  }

  private setupProductSubscriptions(): void {
    this.products$
      .pipe(
        filter((products) => products !== null && products !== undefined),
        takeUntil(this.destroy$)
      )
      .subscribe((products: IProduct[]) => {
        // Skip if products array is empty and we're not loading more (might be initial state)
        if (!products || products.length === 0) {
          // Only skip if we're not actively loading more
          if (!this.isLoadingMore) {
            return;
          }
        }
        
        // Ensure currencies are available before processing
        if (!this.availableCurrencies || this.availableCurrencies.length === 0) {
          // Wait for currencies to be loaded
          this.currencies$.pipe(take(1), takeUntil(this.destroy$)).subscribe((currencies) => {
            this.availableCurrencies = currencies.filter((currency) => 
              this.data?.enabled_currencies?.includes(currency.id)
            ) || [];
            this.processProducts(products);
          });
          return;
        }
        
        this.processProducts(products);
      });

    this.productsMetadata$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe((metadata: IPagination | null) => {
        this.productsMetadata = metadata;
      });

    this.store
      .select((state) => state.products.productFilterState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((filterState) => {
        this.filter = filterState.advanced;
        this.searchTerm = filterState.keyword || '';
        this.currentPage = 1;
        this.isLoadingMore = false;
        this.productsFromSearch = [];
        this.rebuildProductsArray();

        if (this.isCatalogDataLoaded) {
          const filterRequest = parseInventoryFilterRequest(filterState.advanced);
          this.store.dispatch(
            FetchProductsBySearch({
              keyword: filterState.keyword || '',
              page: { number: this.currentPage, size: this.pageSize },
              filter: filterRequest,
              sort_by: 'name',
              sort_mode: 'asc',
              priority: this.getPriorityProductIds(),
              includeDeals: false,
            })
          );
        }
      });
    
    this.actions$.pipe(
      ofType(UpdateCatalogSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      const path = this.source === 'catalogs'
        ? ['/catalogs', 'edit-catalog', this.catalogId]
        : ['/agents', 'edit-catalog', this.catalogId];
      const queryParams = this.source === 'agents' ? { agentId: this.agentId, mode: 'add-products' } : { mode: 'add-products' };
      this.router.navigate(path, { queryParams });
    });
  }

  private processProducts(products: IProduct[]): void {
    // Process all products from search first
    const processedProducts = products
      .map((p: IProduct) => {
        // Initialize currencies if missing
        if (!p.currencies || p.currencies.length === 0) {
          p.currencies = this.availableCurrencies.map((currency) => ({
            currency_id: currency.id,
            currency_name: currency.name,
            sales_price: p.sales_price || undefined,
            final_price: undefined,
          }));
        }
        return {...p, discount: p.discount || this.catalogsService.getNewCatalogData()?.overall_pricing_value || 0};
      })
      .map((p: IProduct) => this.updateAllCurrenciesFinalPrice(p, p.discount || 0));
    
    // Track products from search separately
    // Note: We don't filter out selected products here because:
    // 1. After "select all", all products are selected, so filtering would remove all search results
    // 2. The rebuildProductsArray method handles deduplication properly
    // 3. Users should be able to see search results even if products are selected
    const newProductsFromSearch: IProduct[] = [];
    
    // Add all processed products to search results
    // The rebuildProductsArray will handle showing them correctly
    processedProducts.forEach((product: IProduct) => {
      const id = product.id ?? product.product_id ?? 0;
      if (id > 0) {
        newProductsFromSearch.push(product);
      }
    });
    
    if (this.isLoadingMore) {
      // When loading more, add new products from search
      const existingProductIds = new Set(
        this.productsFromSearch.map(p => p.id ?? p.product_id ?? 0).filter(id => id > 0)
      );
      
      // Add new products from search that aren't already loaded
      newProductsFromSearch.forEach((product: IProduct) => {
        const id = product.id ?? product.product_id ?? 0;
        if (id > 0 && !existingProductIds.has(id)) {
          this.productsFromSearch.push(product);
          existingProductIds.add(id);
        }
      });
      
      this.isLoadingMore = false;
    } else {
      // First load or search: Replace products from search
      this.productsFromSearch = [...newProductsFromSearch];
    }
    // Auto-select all products if flag is set (select products as they load when in "select all" mode)
    if (this.hasSelectedAllProducts && this.selectableProductsListComponent) {
      const batchIds = new Set<number>(
        processedProducts
          .map((p) => p.product_id ?? p.id ?? 0)
          .filter((id) => id > 0)
      );
      setTimeout(() => {
        this.selectableProductsListComponent.selectAllValidProducts(batchIds);
        // Keep loading more pages until all products are loaded and selected (no need for user to scroll)
        const totalCount = this.productsMetadata?.count ?? 0;
        if (
          !this.isSelectingAll &&
          !this.isLoadingMore &&
          totalCount > 0 &&
          this.currentPage * this.pageSize < totalCount
        ) {
          this.loadMore();
        }
      }, 200);
    }
    // Rebuild products array: selected from catalog + products from search
    this.rebuildProductsArray();
  }

  private setupCatalogData(): void {}

  private updateAllCurrenciesFinalPrice(product: IProduct, discount: number): IProduct {
    // Initialize currencies if missing and we have available currencies
    if (!product.currencies || product.currencies.length === 0) {
      if (this.availableCurrencies && this.availableCurrencies.length > 0) {
        product.currencies = this.availableCurrencies.map((currency) => ({
          currency_id: currency.id,
          currency_name: currency.name,
          sales_price: product.sales_price || undefined,
          final_price: product.sales_price ? Number((product.sales_price * (1 - discount / 100)).toFixed(2)) : undefined,
        }));
      } else {
        // No currencies available, return product as-is to avoid errors
        return product;
      }
    }

    const currencies: IProductCurrency[] = product.currencies.map((currency) => {
      if (!currency.final_price && currency.sales_price) {
        const finalPrice = currency.sales_price * (1 - discount / 100);
        return { ...currency, final_price: Number(finalPrice.toFixed(2)) };
      }
      return currency;
    });
    return { ...product, currencies };
  }

  hasDraftSelectedProducts(): boolean {
    return this.selectedProducts.some(p => p.status === 'Draft');
  }

  loadMore(): void {
    if (
      this.isSelectingAll 
      || this.isLoadingMore 
      || !this.productsMetadata 
      || (this.currentPage * this.pageSize >= this.productsMetadata.count)
    ) return;

    this.isLoadingMore = true;
    this.currentPage += 1;
    const filterRequest = parseInventoryFilterRequest(this.filter);
    this.store.dispatch(
      FetchProductsBySearch({
        page: { number: this.currentPage, size: this.pageSize },
        filter: filterRequest,
        keyword: this.searchTerm || '',
        sort_by: 'name',
        sort_mode: 'asc',
        priority: this.getPriorityProductIds(),
        includeDeals: false, // Skip deals data for faster product selection
      })
    );
  }

  onSearchChange(searchTerm: string) {
    // Reset to first page when searching
    this.currentPage = 1;
    this.isLoadingMore = false;
    this.hasSelectedAllProducts = false;
    
    // Clear products from search when searching
    this.productsFromSearch = [];
    
    // Rebuild products array with just selected products
    this.rebuildProductsArray();
    
    // Update search keyword
    this.searchTerm = searchTerm;
    
    // Dispatch search action to update store
    const filterRequest = parseInventoryFilterRequest(this.filter);
    this.store.dispatch(
      FetchProductsBySearch({
        keyword: this.searchTerm || '',
        page: { number: this.currentPage, size: this.pageSize },
        filter: filterRequest,
        sort_by: 'name',
        sort_mode: 'asc',
        priority: this.getPriorityProductIds(),
        includeDeals: false, // Skip deals data for faster product selection
      })
    );
  }

  onSelectAllRequested() {
    if (!this.canSubmit) return;
    // Prevent multiple simultaneous select all operations
    if (this.isSelectingAll) {
      return;
    }

    if (!this.productsMetadata?.count) {
      this.isSelectingAll = false;
      return;
    }

    // Immediately add all currently visible product IDs to selectedProductIds
    // This ensures the checkbox shows as checked right away
    const visibleProductIds = this.products
      .filter((product: IProduct) => {
        // Check if product is valid (similar to isProductValid logic)
        if (product.status === 'Draft') return false;
        if (!product.enabled) return false;
        return true;
      })
      .map((product: IProduct) => product.product_id ?? product.id ?? 0)
      .filter((id): id is number => id > 0);
    
    // Add visible product IDs to selectedProductIds immediately
    visibleProductIds.forEach(id => this.selectedProductIds.add(id));
    
    // Update child component's selectedProductIds immediately so checkbox shows as checked
    if (this.selectableProductsListComponent) {
      this.selectableProductsListComponent.selectedProductIds = new Set([...this.selectedProductIds]);
      this.selectableProductsListComponent.runChangeDetection();
    }

    this.hasSelectedAllProducts = true;
    this.isSelectingAll = true;
    this.selectingAllMessage = this.translateService.instant('Catalogs.new_catalog.products.select_all.preparing_to_load');

    this.selectingAllMessage = this.translateService.instant('Catalogs.new_catalog.products.select_all.selecting_products');
    console.log('Fetching all products');
    // Fetch all products safely
    this.fetchAllProductsSafely(this.productsMetadata.count ?? 0);
  }

  onDeselectAllRequested() {
    // Clear the flag so newly loaded products won't be auto-selected
    this.hasSelectedAllProducts = false;
  }

  /**
   * Fetch all products using the optimized "select all" endpoint.
   * This endpoint returns minimal data in a single call for maximum performance.
   * Replaces the batch fetching approach to prevent crashes and improve performance.
   */
  private fetchAllProductsSafely(totalCount: number) {
    this.selectingAllMessage = this.translateService.instant('Catalogs.new_catalog.products.select_all.loading_all_products');
    
    const filterRequest = parseInventoryFilterRequest(this.filter);

    // Use the new optimized endpoint for "select all"
    // This endpoint uses direct SQL queries and returns minimal data
    this.productsService.fetchAllProductsForSelectAll(
      filterRequest,
      this.searchTerm || undefined
    ).pipe(
      take(1),
      takeUntil(this.destroy$),
      catchError(() => {
        this.isSelectingAll = false;
        this.selectingAllMessage = '';
        return of({ data: [], metadata: null } as any);
      })
    ).subscribe({
      next: (response: { data: IProduct[]; metadata: any }) => {
        if (!response.data?.length) {
          this.isSelectingAll = false;
          this.selectingAllMessage = '';
          return;
        }

        // For large datasets (5k+ products), use lightweight ID-based selection
        // Instead of processing all products immediately, just select their IDs
        if (this.products.length > (this.productsMetadata?.count || 0)) {
          this.selectAllProductsByIds(response.data);
          return;
        }

        // For smaller datasets, process normally but in batches
        this.processProductsInBatches(response.data);
      },
      error: () => {
        this.isSelectingAll = false;
        this.selectingAllMessage = '';
      }
    });
  }

  /**
   * Select all products by IDs only (lightweight approach for large datasets > 10k).
   * This avoids processing thousands of product objects in memory.
   */
  private selectAllProductsByIds(allProducts: IProduct[]) {
    this.selectingAllMessage = this.translateService.instant('Catalogs.new_catalog.products.select_all.processing_product_ids');
    
    // Extract only IDs from products (minimal memory usage)
    const productIds = new Set<number>();
    let validCount = 0;
    
    allProducts.forEach((product: IProduct) => {
      const productId = product.product_id ?? product.id ?? 0;
      if (productId > 0) {
        productIds.add(productId);
        validCount++;
      }
    });

    // New Set reference so child's @Input() detects change and checkboxes update
    this.selectableProductsListComponent.selectedProductIds = new Set([...productIds]);
    this.cdr.detectChanges();

    // For very large datasets, we'll store minimal product data
    // Process in batches to avoid memory issues
    const batchSize = 1000;
    let processed = 0;
    const minimalProducts: IProduct[] = [];
    
    const processBatch = () => {
      const batch = allProducts.slice(processed, processed + batchSize);
      const totalBatches = Math.ceil(allProducts.length / batchSize);
      const currentBatch = Math.floor(processed / batchSize) + 1;
      
      this.selectingAllMessage = this.translateService.instant('Catalogs.new_catalog.products.select_all.processing_products', {
        current: currentBatch,
        total: totalBatches
      });
      
      const discount = this.catalogsService.getNewCatalogData()?.overall_pricing_value ?? 0;
      const discountMultiplier = 1 - discount / 100;

      batch.forEach((product: IProduct) => {
        const productId = product.product_id ?? product.id ?? 0;
        if (productId > 0 && this.selectedProductIds.has(productId)) {
          // Initialize currencies with final_price set so validation passes (final price not set)
          let currencies = product.currencies && product.currencies.length > 0
            ? product.currencies.map((c: any) => ({
                ...c,
                sales_price: c.sales_price ?? product.sales_price,
                final_price: c.final_price ?? (c.sales_price ?? product.sales_price)
                  ? Number(((c.sales_price ?? product.sales_price) * discountMultiplier).toFixed(2))
                  : undefined,
              }))
            : (this.availableCurrencies?.map((currency) => ({
                currency_id: currency.id,
                currency_name: currency.name,
                sales_price: product.sales_price ?? undefined,
                final_price: product.sales_price
                  ? Number((product.sales_price * discountMultiplier).toFixed(2))
                  : undefined,
              })) ?? []);
          
          // Store minimal product data with currencies so validation (final price per currency) passes
          minimalProducts.push({
            id: product.id,
            product_id: product.product_id,
            name: product.name || '',
            sku: product.sku || '',
            status: product.status || 'Draft',
            enabled: product.enabled !== false,
            product_template_id: (product as any).product_template_id,
            discount,
            currencies,
          } as IProduct);
        }
      });
      
      processed += batchSize;
      
      if (processed < allProducts.length) {
        // Process next batch asynchronously to avoid blocking UI
        requestAnimationFrame(() => {
          setTimeout(processBatch, 10);
        });
      } else {
        // All batches processed - update selected products
        this.selectedProducts = minimalProducts;
        
        // Update child component if available
        if (this.selectableProductsListComponent) {
          this.selectableProductsListComponent.selectedProducts = [...this.selectedProducts];
          this.selectableProductsListComponent.emitSelectedProducts();
          this.selectableProductsListComponent.runChangeDetection();
        }
        
        // Do not change the loaded products list - keep productsFromSearch as-is so the visible list stays the same
        this.rebuildProductsArray();
        
        this.cdr.detectChanges();
        
        this.selectingAllMessage = this.translateService.instant('Catalogs.new_catalog.products.select_all.successfully_selected', {
          count: minimalProducts.length
        });
        setTimeout(() => {
          this.isSelectingAll = false;
          this.selectingAllMessage = '';
        }, 500);
      }
    };

    processBatch();
  }

  /**
   * Process products in batches for smaller datasets (< 10k).
   * Builds selection only; does not change the loaded products list (productsFromSearch stays unchanged).
   */
  private processProductsInBatches(allProducts: IProduct[]) {
    this.selectingAllMessage = this.translateService.instant('Catalogs.new_catalog.products.select_all.processing_products_batches');
    
    const discount = this.catalogsService.getNewCatalogData()?.overall_pricing_value || 0;
    const discountMultiplier = 1 - discount / 100; // Pre-calculate multiplier to avoid repeated division
    
    const batchSize = 500;
    let processed = 0;
    const selectedProductsFromSelectAll: IProduct[] = [];
    const totalBatches = Math.ceil(allProducts.length / batchSize);
    const startTime = Date.now();
    const maxProcessingTime = 60000;

    const processBatch = () => {
      if (Date.now() - startTime > maxProcessingTime) {
        this.selectedProducts = selectedProductsFromSelectAll;
        selectedProductsFromSelectAll.forEach((p) => {
          const id = p.id ?? p.product_id ?? 0;
          if (id > 0) this.selectedProductIds.add(id);
        });
        if (this.selectableProductsListComponent) {
          this.selectableProductsListComponent.selectedProductIds = new Set([...this.selectedProductIds]);
          // this.selectableProductsListComponent.emitSelectedProducts();
        }
        this.rebuildProductsArray();
        this.cdr.detectChanges();
        this.finalizeSelection();
        return;
      }

      try {
        const batch = allProducts.slice(processed, processed + batchSize);
        const currentBatch = Math.floor(processed / batchSize) + 1;
        for (let i = 0; i < batch.length; i++) {
          const product = batch[i];
          product.discount = discount;
          if (!product.currencies || product.currencies.length === 0) {
            product.currencies = this.availableCurrencies?.map((currency) => ({
              currency_id: currency.id,
              currency_name: currency.name,
              sales_price: product.sales_price || undefined,
              final_price: product.sales_price ? Number((product.sales_price * discountMultiplier).toFixed(2)) : 0,
            })) || [];
          } else {
            product.currencies = product.currencies?.map((currency) => (currency.final_price ? currency : {
              ...currency,
              final_price: currency.sales_price ? Number((currency.sales_price * discountMultiplier).toFixed(2)) : 0,
            })) || [];
          }
          selectedProductsFromSelectAll.push(product);
        }

        processed += batch.length;

        if (processed >= allProducts.length || batch.length === 0) {
          this.selectingAllMessage = this.translateService.instant('Catalogs.new_catalog.products.select_all.products_loaded_selecting');
          this.selectedProducts = selectedProductsFromSelectAll;
          selectedProductsFromSelectAll.forEach((p) => {
            const id = p.id ?? p.product_id ?? 0;
            if (id > 0) this.selectedProductIds.add(id);
          });
          if (this.selectableProductsListComponent) {
            this.selectableProductsListComponent.selectedProductIds = new Set([...this.selectedProductIds]);
            this.selectableProductsListComponent.selectedProducts = [...this.selectedProducts];
            this.selectableProductsListComponent.emitSelectedProducts();
            this.selectableProductsListComponent.runChangeDetection();
          }
          // Do not change productsFromSearch - keep the loaded products list as-is
          this.rebuildProductsArray();
          this.cdr.detectChanges();
          this.finalizeSelection();
        } else {
          setTimeout(processBatch, 0);
        }
      } catch {
        this.rebuildProductsArray();
        this.selectAllLoadedProducts();
      }
    };

    processBatch();
  }

  /**
   * Select all valid products that are currently loaded.
   * Optimized to create form controls and select products in a single pass.
   */
  private selectAllLoadedProducts() {
    try {
      if (!this.selectableProductsListComponent) {
        this.isSelectingAll = false;
        return;
      }

      if (!this.products || this.products.length === 0) {
        this.isSelectingAll = false;
        return;
      }

      // Optimized: Create form controls and collect valid products in a single pass
      const allValidProducts: IProduct[] = [];
      const form = this.selectableProductsListComponent.productsForm;
      const controlBatchSize = 200; // Increased batch size for better performance
      let controlIndex = 0;
      const totalBatches = Math.ceil(this.products.length / controlBatchSize);

      const processBatch = () => {
        const batch = this.products.slice(controlIndex, controlIndex + controlBatchSize);
        const currentBatch = Math.floor(controlIndex / controlBatchSize) + 1;
        
        // Update UI every 3 batches to reduce change detection overhead
        if (currentBatch % 3 === 1 || currentBatch === totalBatches) {
          this.selectingAllMessage = this.translateService.instant('Catalogs.new_catalog.products.select_all.preparing_products', {
            current: currentBatch,
            total: totalBatches
          });
        }
        
        // Process batch: create controls and collect valid products
        for (const product of batch) {
          const productId = product.product_id ?? product.id ?? 0;
          
          if (productId > 0) {
            // Create form control if it doesn't exist
            if (!form.contains(`final_price_${productId}`)) {
              try {
                this.selectableProductsListComponent.createControlsForProduct(product);
              } catch (error) {
                // Continue even if control creation fails
              }
            }
            allValidProducts.push(product);
          }
        }

        controlIndex += controlBatchSize;

        if (controlIndex < this.products.length) {
          // Process next batch asynchronously
          requestAnimationFrame(() => {
            setTimeout(processBatch, 0);
          });
        } else {
          // All products processed - proceed with selection
          this.selectingAllMessage = this.translateService.instant('Catalogs.new_catalog.products.select_all.selecting_count', {
            count: allValidProducts.length
          });
          
          if (allValidProducts.length > 0) {
            this.processProductSelection(allValidProducts);
          } else {
            this.isSelectingAll = false;
          }
        }
      };

      // Start processing
      processBatch();
    } catch {
      this.isSelectingAll = false;
    }
  }

  /**
   * Process product selection in batches to avoid UI freezing.
   * Optimized with Set-based duplicate checking for O(1) lookups.
   */
  private processProductSelection(allValidProducts: IProduct[]) {
    try {
      // Use Set for O(1) duplicate checking instead of O(n) array.some()
      const selectedIds = new Set<number>();
      this.selectedProducts.forEach(p => {
        const id = p.id ?? p.product_id ?? 0;
        if (id > 0) selectedIds.add(id);
      });
      
      // Increased batch size for better performance
      const batchSize = 200;
      let currentIndex = 0;
      const totalBatches = Math.ceil(allValidProducts.length / batchSize);
      const newSelectedProducts: IProduct[] = [];

      const processBatch = () => {
        const batch = allValidProducts.slice(currentIndex, currentIndex + batchSize);
        const currentBatch = Math.floor(currentIndex / batchSize) + 1;
        
        // Update UI every 2 batches
        if (currentBatch % 2 === 1 || currentBatch === totalBatches) {
          if (totalBatches > 1) {
            this.selectingAllMessage = this.translateService.instant('Catalogs.new_catalog.products.select_all.processing_products', {
              current: currentBatch,
              total: totalBatches
            });
          } else {
            this.selectingAllMessage = this.translateService.instant('Catalogs.new_catalog.products.select_all.selecting_products');
          }
        }
        
        // Process batch: add products that aren't already selected
        for (const product of batch) {
          const productId = product.id ?? product.product_id ?? 0;
          if (productId > 0 && !selectedIds.has(productId)) {
            selectedIds.add(productId);
            newSelectedProducts.push(product);
          }
        }

        currentIndex += batchSize;

        if (currentIndex < allValidProducts.length) {
          // Process next batch asynchronously
          requestAnimationFrame(() => {
            setTimeout(processBatch, 0);
          });
        } else {
          // All products processed - update selected products arrays
          this.selectedProducts.push(...newSelectedProducts);
          
          // Update selectedProductIds Set
          newSelectedProducts.forEach(p => {
            const id = p.id ?? p.product_id ?? 0;
            if (id > 0) this.selectedProductIds.add(id);
          });
          
          // Update child component
          if (this.selectableProductsListComponent) {
            this.selectableProductsListComponent.selectedProducts = [...this.selectedProducts];
            this.selectableProductsListComponent.emitSelectedProducts();
          }
          
          // Finalize
          this.finalizeSelection();
        }
      };

      // Start processing batches
      processBatch();
    } catch {
      this.isSelectingAll = false;
    }
  }

  /**
   * Finalize the selection process and emit changes.
   * Optimized: duplicates are already handled in processProductSelection.
   */
  private finalizeSelection() {
    try {
      const finalCount = this.selectedProducts.length;
      
      // Ensure child component's selectedProducts and selectedProductIds are in sync
      if (this.selectableProductsListComponent) {
        this.selectableProductsListComponent.selectedProducts = [...this.selectedProducts];
        // Update selectedProductIds in child component to ensure checkbox shows correctly
        this.selectableProductsListComponent.selectedProductIds = new Set([...this.selectedProductIds]);
        this.selectableProductsListComponent.emitSelectedProducts();
        this.selectableProductsListComponent.runChangeDetection();
      }
      
      // Show completion message
      this.selectingAllMessage = this.translateService.instant('Catalogs.new_catalog.products.select_all.successfully_selected', {
        count: finalCount
      });
      
      // Clear loading state after brief delay
      setTimeout(() => {
        this.isSelectingAll = false;
        this.selectingAllMessage = '';
      }, 300);
    } catch {
      this.isSelectingAll = false;
      this.selectingAllMessage = '';
    }
  }

  setNewCatalogProducts() {
    if (!this.canSubmit) return;
    this.catalogsService.setNewCatalogData({ products: this.selectedProducts });
    if(this.catalogId){
      this.update()
      return;
    }
    this.goBack();
  }

  update(){
    if (!this.canSubmit) return;
    const data = this.catalogsService.getNewCatalogData()
    if(!data) return;
    
    const isMarginMode = data.pricing_rule === 'margin';
    const isSingleCurrency = !data.is_multi_currency;
    const supportedCurrencyIds = isSingleCurrency 
      ? (data.default_currency ? [data.default_currency] : [])
      : (data.enabled_currencies && data.enabled_currencies.length > 0 
          ? data.enabled_currencies 
          : (data.default_currency ? [data.default_currency] : []));
    
    // Pre-compute enabled currencies set for O(1) lookup instead of O(n) array.includes()
    const enabledCurrenciesSet = new Set([
      ...(data.enabled_currencies || []),
      data.default_currency
    ].filter(id => id !== undefined && id !== null));
    
    const products = this.selectedProducts?.map((product: IProduct) => {
      // Optimized currency filtering using Set lookup
      let currencies = product.currencies?.filter((currency: ICurrencyPrice) => 
        currency.currency_id !== undefined && 
        currency.currency_id !== null &&
        enabledCurrenciesSet.has(currency.currency_id)
      );
      const hasCustomDiscount = (product as any).hasCustomDiscount || false;
      const productDiscountValue = product.discount ?? 0;
      const overallPricingValue = data.overall_pricing_value ?? 0;
      const isCustomDiscount = hasCustomDiscount || (productDiscountValue !== 0 && productDiscountValue !== overallPricingValue);
      const productDiscount = isCustomDiscount ? productDiscountValue : (data.overall_pricing_value ?? 0);
      
      const defaultCurrency = currencies?.find(c => c.currency_id === data.default_currency);
      
      let productFinalPrice = isCustomDiscount && product.final_price && product.final_price > 0 
        ? product.final_price 
        : (defaultCurrency?.final_price ?? product.final_price ?? 0);
      
      if (this.source === 'agents' && currencies && !isCustomDiscount) {
        currencies = currencies.map((currency: IProductCurrency) => {
          if (currency.final_price !== undefined && currency.final_price !== null && currency.final_price > 0) {
            return currency;
          }
          
          if (isMarginMode) {
            const currencyCost = currency.cost ?? product.cost ?? 0;
            if (currencyCost && currencyCost > 0 && productDiscount !== undefined && productDiscount !== null) {
              const calculatedFinalPrice = Number((currencyCost * (1 + productDiscount / 100)).toFixed(2));
              return {
                ...currency,
                final_price: calculatedFinalPrice
              };
            } else {
              const formFinalPrice = product.final_price || productFinalPrice || 0;
              return {
                ...currency,
                final_price: formFinalPrice > 0 ? Number(formFinalPrice) : currency.final_price || 0
              };
            }
          } else {
            const currencySalesPrice = currency.sales_price ?? product.sales_price ?? 0;
            if (currencySalesPrice > 0 && productDiscount !== undefined && productDiscount !== null) {
              const calculatedFinalPrice = Number((currencySalesPrice * (1 - productDiscount / 100)).toFixed(2));
              return {
                ...currency,
                final_price: calculatedFinalPrice
              };
            } else {
              return {
                ...currency,
                final_price: productFinalPrice || currency.final_price || 0
              };
            }
          }
        });
        
        const updatedDefaultCurrency = currencies.find(c => c.currency_id === data.default_currency);
        if (updatedDefaultCurrency?.final_price !== undefined && updatedDefaultCurrency?.final_price !== null) {
          productFinalPrice = Number(updatedDefaultCurrency.final_price);
        }
      }
      
      const { final_price, currencies: productCurrencies, ...rest } = product;
      const handlingUnits = product.handling_units || [];
      
      // Optimized handling unit processing - single pass instead of multiple filters/maps
      let enabledHandlingUnitIds: number[] = [];
      let defaultHU: any = null;
      
      for (const hu of handlingUnits) {
        if (hu.enabled !== false) {
          if (hu.id !== undefined) {
            enabledHandlingUnitIds.push(hu.id);
          }
          // Find default in the same pass
          if (!defaultHU && hu.default === true) {
            defaultHU = hu;
          }
        }
      }
      
      const productPayload: any = {
        ...rest,
        id: Number(product.id),
        max_extra_discount: product.extra_discount,
        enabled_handling_unit_ids: enabledHandlingUnitIds,
        default_handling_unit_id: defaultHU?.id || null,
      };
      
      if (this.source === 'agents') {
        productPayload.final_price = productFinalPrice;
        productPayload.discount = productDiscount;
      } else {
        productPayload.currencies = currencies;
      }
      
      return productPayload;
    })

    const payload: Omit<ICatalog, 'id'> = {
      subsidiary_id: data.subsidiary_id || undefined,
      name: data.name,
      description: data.description,
      image: data.coverUrl ?? '',
      products,
      locations_ids: data.locations_ids || [],
      end_date: data.expiration_date ? new Date(data.expiration_date).toISOString().split('T')[0] : null,
      status: data.status || 'Draft',
      overall_pricing_rule: data.pricing_rule === 'margin' ? 'margin_on_cost' : 'discount_on_msrp',
      overall_pricing_value: data.overall_pricing_value || 0,
      increment: data.quantity_rules?.increment || 1,
      max_qte: data.quantity_rules?.max_qte || null,
      min_qte: data.quantity_rules?.min_qte || 1,
      can_be_sold_out_of_stock: data.quantity_rules?.can_be_sold_out_of_stock || false,
      show_stock: data.quantity_rules?.show_stock === true ? true : false,
      is_single_currency: !data.is_multi_currency,
      default_currency_id: data.default_currency,
      currency_ids: data.enabled_currencies,
      enabled_handling_unit_ids: data.enabled_handling_unit_ids || [],
      default_handling_unit_id: data.default_handling_unit_id || null,
      show_full_pricing_data: data.show_full_pricing_data ?? true,
    }
    this.store.dispatch(UpdateCatalog({id: this.catalogId, catalog: payload}))
  }

  onSelectedProductsChange(selectedProducts: IProduct[]): void {
    this.selectedProducts = selectedProducts;
    const newSelectedProductIds = new Set(
      selectedProducts
        .map(p => p.id ?? p.product_id ?? 0)
        .filter(id => id > 0)
    );
    
    // If count decreased and we were in "select all" mode, clear the flag
    if (this.hasSelectedAllProducts && newSelectedProductIds.size < this.selectedProductIds.size) {
      this.hasSelectedAllProducts = false;
    }
    
    // Always update selectedProductIds to ensure count is accurate
    this.selectedProductIds = newSelectedProductIds;
    this.sortProductsWithSelectedFirst();
    
    // Trigger change detection to ensure child component updates
    this.cdr.detectChanges();
  }

  private rebuildProductsArray(): void {
    const processedSelectedProducts: IProduct[] = [];
    const productId = (p: IProduct) => p.id ?? p.product_id ?? 0;
    const hasSearchTerm = this.searchTerm && this.searchTerm.trim().length > 0;
    const hasActiveFilters = this.isFilterActive();

    // If there's a search term or active filters, only show filtered/search results (not original catalog products)
    if (hasSearchTerm || hasActiveFilters) {
      // Only show products from search/filter when searching or filtering
      processedSelectedProducts.push(...this.productsFromSearch.filter(p => productId(p) > 0));
    } else {
      // No search or filters: show selected products from catalog first, then add search results
      processedSelectedProducts.push(...(this.selectedProductsFromCatalog.filter(p => productId(p) > 0).map(p => ({
        ...p,
        discount: p.discount ?? this.data?.overall_pricing_value ?? 0
      }))));
      const productIds = new Set(processedSelectedProducts.map(productId));
      processedSelectedProducts.push(...this.productsFromSearch.filter(p => productId(p) > 0 && !productIds.has(productId(p))));
    }
    
    // Set all products
    this.products = processedSelectedProducts.map(p => this.updateAllCurrenciesFinalPrice(p, p.discount ?? 0));
    this.currentPage = this.currentPage === 1 ? Math.floor(this.products.length / this.pageSize) : this.currentPage;

    this.sortProductsWithSelectedFirst();
  }

  private sortProductsWithSelectedFirst(): void {
    if (this.selectedProductIds.size === 0) return;
    
    this.products.sort((a, b) => {
      const aId = a.id ?? a.product_id ?? 0;
      const bId = b.id ?? b.product_id ?? 0;
      const aIsSelected = this.selectedProductIds.has(aId);
      const bIsSelected = this.selectedProductIds.has(bId);
      
      // Selected products come first
      if (aIsSelected && !bIsSelected) return -1;
      if (!aIsSelected && bIsSelected) return 1;
      
      // If both are selected or both are not selected, maintain original order (name sort)
      return 0;
    });
  }

  private getPriorityProductIds(): number[] {
    return Array.from(this.selectedProductIds);
  }

  openFilter(): void {
    const dialogRef = this.dialog.open(FilterDialogComponent, {
      minWidth: '650px',
      width: '80vw',
      height: '80vh',
    });
    dialogRef.afterClosed().subscribe((filter: IInventoryFilter) => {
      if (filter) {
        this.filter = filter;
        this.hasSelectedAllProducts = false;
        this.store.dispatch(
          SetInventoryFilter({
            keyword: '',
            advanced: filter,
          })
        );
      }
    });
  }

  clearFilter(): void {
    this.hasSelectedAllProducts = false;
    this.store.dispatch(
      SetInventoryFilter({
        keyword: '',
        advanced: refillInventoryFilter(),
      })
    );
  }

  isFilterActive(): boolean {
    return parseInventoryFilterRequest(this.filter)?.filters?.length > 0;
  }

  goBack(): void {
    if (this.source === 'agents') {
      if(this.catalogId) {
        this.router.navigate(['/agents', 'edit-catalog', this.catalogId], { queryParams: { agentId: this.agentId, mode: 'add-products' } });
      }
      else {
        this.router.navigate(['/agents', 'new-catalog'], { queryParams: { agentId: this.agentId, mode: 'add-products' } });
      }
    } else {
      if(this.catalogId){
        this.router.navigate(['/catalogs', 'edit-catalog', this.catalogId], { queryParams: { mode: 'add-products' } });
      } else {
        this.router.navigate(['/catalogs', 'new-catalog'], { queryParams: { mode: 'add-products' } });
      }
    }
  }

  hasInvalidPrices(): boolean {
    if (!this.selectedProducts.length) return true;
    
    const hasInvalid = this.selectedProducts.some((p) => {
      if (p.currencies && p.currencies.length > 0) {
        const hasValidCurrencyPrice = p.currencies.some(
          (c) => (c.sales_price ?? 0) > 0 || (c.final_price ?? 0) > 0
        );
        return !hasValidCurrencyPrice;
      }
      
      return (p.sales_price ?? 0) === 0 && (p.final_price ?? 0) === 0;
    });
    
    return hasInvalid;
  }

  getValidationTooltipMessage(): string {
    const pricingRule = this.data?.pricing_rule === 'margin' ? 'margin_on_cost' : 'discount_on_msrp';
    return getValidationTooltipMessage(
      this.selectedProducts,
      this.availableCurrencies,
      this.translateService,
      this.source,
      pricingRule,
      this.data?.default_currency
    );
  }

  areSelectedProductsValid(): boolean {
    const pricingRule = this.data?.pricing_rule === 'margin' ? 'margin_on_cost' : 'discount_on_msrp';
    return areSelectedProductsValid(
      this.selectedProducts,
      this.availableCurrencies,
      this.translateService,
      this.source,
      pricingRule,
      this.data?.default_currency
    );
  }

  areProductsValidEmitter(event: boolean): void {
    this.areProductsValid = event;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearFilter();
    this.store.dispatch(ClearProducts());
  }

  get canSubmit(): boolean {
    return this.catalogId ? this.canEdit : this.canCreate;
  }

  submitPermission(): HasPermissionContext {
    return { module: 'catalog', action: this.catalogId ? 'edit' : 'create' };
  }
}
